import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { MATCHING_ENABLED } from '@/config/features';
import { formatDistance, DEFAULT_USER_LOCATION, searchRegionCoordinates, getRegionCoordinate, getPatientDistance } from '@/hooks/distance';

type PatientItem = {
  id: string;
  userId: string;
  name: string;
  age: number;
  gender: string;
  hospital: string;
  disease: string;
  careStartDate: string;
  region: string;
  careLocation: string;
  locationType: '집' | '병원' | '';
  latitude: number;
  longitude: number;
  status: string;
};

type SortMode = 'compound' | 'distance' | 'start_date';

const regionFilter = ['전체', '서울', '경기', '인천', '부산', '대구'];

// home_address는 JSON 문자열(다음 주소 객체)로 저장되므로 도로명/지번 주소를 뽑아낸다
function parseHomeAddress(raw: string | null): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return parsed.roadAddress || parsed.jibunAddress || '';
  } catch {
    return raw;
  }
}

export default function AppCaregiverSearchPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [allPatients, setAllPatients] = useState<PatientItem[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');

  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('전체');
  const [sortMode, setSortMode] = useState<SortMode>('compound');
  const [myLocation, setMyLocation] = useState(DEFAULT_USER_LOCATION);
  const [locationInput, setLocationInput] = useState('');
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationFocused, setLocationFocused] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientItem | null>(null);
  const [proposalForm, setProposalForm] = useState({
    startDate: '',
    endDate: '',
    hourlyRate: '15000',
    dailyHours: '8',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const locationSuggestions = useMemo(() => searchRegionCoordinates(locationInput), [locationInput]);

  function locateMe() {
    if (!navigator.geolocation) {
      setLocationError('이 브라우저는 위치 정보를 지원하지 않아요.');
      return;
    }
    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: '내 현재 위치' });
        setLocationInput('');
        setLocationFocused(false);
        setLocating(false);
      },
      () => {
        setLocationError('위치 권한이 거부되었어요. 지역을 직접 입력해주세요.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  function applyLocationInput() {
    const text = locationInput.trim();
    if (!text) return;
    let coord = getRegionCoordinate(text);
    if (!coord) {
      const found = searchRegionCoordinates(text)[0];
      if (found) coord = { lat: found.lat, lng: found.lng };
    }
    if (coord) {
      setMyLocation({ lat: coord.lat, lng: coord.lng, label: text });
      setLocationInput('');
      setLocationFocused(false);
      setLocationError('');
    } else {
      setLocationError('지역을 찾을 수 없어요. (예: 서울 강남구)');
    }
  }

  function selectLocationSuggestion(s: { key: string; lat: number; lng: number }) {
    setMyLocation({ lat: s.lat, lng: s.lng, label: s.key });
    setLocationInput('');
    setLocationFocused(false);
    setLocationError('');
  }

  useEffect(() => {
    loadPatients();
  }, []);

  async function loadPatients() {
    setDataLoading(true);
    setDataError('');
    try {
      const { data: requests, error: reqErr } = await supabase
        .from('patient_care_requests')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (reqErr) throw reqErr;
      if (!requests || requests.length === 0) {
        setAllPatients([]);
        setDataLoading(false);
        return;
      }

      const userIds = requests.map((r) => r.user_id).filter(Boolean);
      let profileMap = new Map<string, { name: string; gender: string; birth_date: string; latitude: number; longitude: number }>();
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, gender, birth_date, latitude, longitude')
          .in('id', userIds);
        (profiles || []).forEach((p) => profileMap.set(p.id, p));
      }

      const mapped: PatientItem[] = requests.map((r) => {
        const profile = profileMap.get(r.user_id);
        const age = profile?.birth_date
          ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear()
          : 0;
        const diseaseLabel = r.additional_notes
          ? r.additional_notes.slice(0, 30) + (r.additional_notes.length > 30 ? '...' : '')
          : '간병 필요';

        return {
          id: r.id,
          userId: r.user_id,
          name: profile?.name || '익명',
          age,
          gender: profile?.gender || '미입력',
          hospital: r.hospital_name || parseHomeAddress(r.home_address) || r.care_region || '미입력',
          disease: diseaseLabel,
          careStartDate: r.care_start_date || '',
          region: r.care_region || '',
          careLocation: r.care_location_type === '집' ? parseHomeAddress(r.home_address) : '',
          locationType: (r.care_location_type === '집' ? '집' : r.care_location_type === '병원' ? '병원' : '') as '집' | '병원' | '',
          latitude: Number(profile?.latitude) || 0,
          longitude: Number(profile?.longitude) || 0,
          status: r.status,
        };
      });

      setAllPatients(mapped);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setDataError('환자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  }

  const filtered = useMemo(() => {
    let results = allPatients.filter((p) => {
      if (selectedRegion !== '전체' && !p.region.includes(selectedRegion)) return false;
      if (search && !p.hospital.includes(search) && !p.disease.includes(search) && !p.name.includes(search)) return false;
      return true;
    });

    results = results.map((p) => ({
      ...p,
      distance: getPatientDistance(myLocation.lat, myLocation.lng, p.latitude, p.longitude, p.region, p.careLocation),
    }));

    if (sortMode === 'compound') {
      // 복합 정렬: 1순위 거리 → 2순위 간병 시작일 빠른 순
      results.sort((a, b) => {
        // 1순위: 거리 (가까운 순)
        if (a.distance !== b.distance) return a.distance - b.distance;

        // 2순위: 간병 필요 시기가 가장 빠른 순
        const dateA = a.careStartDate ? new Date(a.careStartDate).getTime() : Infinity;
        const dateB = b.careStartDate ? new Date(b.careStartDate).getTime() : Infinity;
        return dateA - dateB;
      });
    } else if (sortMode === 'distance') {
      results.sort((a, b) => a.distance - b.distance);
    } else if (sortMode === 'start_date') {
      results.sort((a, b) => {
        const dateA = a.careStartDate ? new Date(a.careStartDate).getTime() : Infinity;
        const dateB = b.careStartDate ? new Date(b.careStartDate).getTime() : Infinity;
        return dateA - dateB;
      });
    }

    return results;
  }, [allPatients, search, selectedRegion, sortMode, myLocation]);

  function getDaysUntilStart(dateStr: string): number {
    if (!dateStr) return 999;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    return Math.max(0, Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  }

  function openProposal(patient: PatientItem) {
    setSelectedPatient(patient);
    setProposalForm({ startDate: patient.careStartDate || '', endDate: '', hourlyRate: '15000', dailyHours: '8', notes: '' });
    setSubmitError('');
    setSubmitSuccess(false);
    setShowProposal(true);
  }

  async function handleSubmitProposal() {
    setSubmitError('');
    if (!MATCHING_ENABLED) {
      setSubmitError('현재 매칭 서비스가 일시 중단되었습니다.');
      return;
    }
    if (!proposalForm.startDate || !proposalForm.endDate) {
      setSubmitError('간병 기간을 입력해주세요.');
      return;
    }
    if (!auth.userId || !selectedPatient) {
      setSubmitError('로그인이 필요합니다.');
      return;
    }

    setSubmitting(true);
    try {
      const days = Math.max(1, Math.ceil(
        (new Date(proposalForm.endDate).getTime() - new Date(proposalForm.startDate).getTime()) / (1000 * 60 * 60 * 24)
      ) + 1);
      const totalAmount = parseInt(proposalForm.hourlyRate) * parseInt(proposalForm.dailyHours) * days;

      const { error: insertError } = await supabase.from('match_requests').insert({
        requester_id: auth.userId,
        receiver_id: selectedPatient.userId,
        requester_role: 'caregiver',
        receiver_role: 'patient',
        status: 'pending',
        start_date: proposalForm.startDate,
        end_date: proposalForm.endDate,
        location: selectedPatient.hospital,
        hourly_rate: parseInt(proposalForm.hourlyRate),
        daily_hours: parseInt(proposalForm.dailyHours),
        total_amount: totalAmount,
        notes: proposalForm.notes || null,
        contact_revealed: false,
      });

      if (insertError) throw insertError;

      setSubmitSuccess(true);
      setTimeout(() => {
        setShowProposal(false);
        setSubmitSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to submit proposal:', err);
      setSubmitError('매칭 제안 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }

  if (auth.loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        <div className="mb-5">
          <h1 className="text-[22px] font-extrabold text-gray-900">환자 찾기</h1>
          <p className="text-sm text-gray-400 mt-1">간병이 필요한 환자를 찾아 제안해보세요</p>
        </div>

        <div className="relative mb-3">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="지역, 병원명, 질환으로 검색"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white"
          />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-3">
          <div className="flex items-center gap-2 mb-2.5">
            <i className="ri-map-pin-line text-[#1976D2]" />
            <span className="text-sm font-bold text-gray-800">내 위치 설정</span>
            <span className="text-[11px] text-gray-400">기준: {myLocation.label}</span>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={locationInput}
                onChange={(e) => { setLocationInput(e.target.value); setLocationError(''); }}
                onFocus={() => setLocationFocused(true)}
                onBlur={() => setTimeout(() => setLocationFocused(false), 150)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyLocationInput(); }}
                placeholder="예: 서울 강남구"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white"
              />
              {locationFocused && locationInput.trim() && locationSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-md z-20 overflow-hidden">
                  {locationSuggestions.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => selectLocationSuggestion(s)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-map-pin-line text-xs text-[#1976D2]" />
                      {s.key}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={locateMe}
              disabled={locating}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-600 cursor-pointer hover:border-[#1976D2] hover:text-[#1976D2] transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <i className={`${locating ? 'ri-loader-4-line animate-spin' : 'ri-crosshair-line'} text-sm`} />
              {locating ? '확인 중' : '내 위치'}
            </button>
            <button
              onClick={applyLocationInput}
              className="px-3 py-2.5 rounded-xl bg-[#1976D2] text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              설정
            </button>
          </div>
          {locationError && (
            <p className="text-[11px] text-red-400 mt-1.5">{locationError}</p>
          )}
        </div>

        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          {regionFilter.map((f) => (
            <button key={f} onClick={() => setSelectedRegion(f)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer border transition-all ${
                f === selectedRegion ? 'bg-[#1976D2] text-white border-[#1976D2]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}>{f}</button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-5 overflow-x-auto">
          <span className="text-xs text-gray-400 mr-1 whitespace-nowrap">정렬:</span>
          {[
            { key: 'compound' as SortMode, label: '종합순', icon: 'ri-sort-desc' },
            { key: 'distance' as SortMode, label: '거리순', icon: 'ri-map-pin-line' },
            { key: 'start_date' as SortMode, label: '시작일 빠른순', icon: 'ri-calendar-check-line' },
          ].map((opt) => (
            <button key={opt.key} onClick={() => setSortMode(opt.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer border transition-all ${
                sortMode === opt.key ? 'bg-[#1976D2] text-white border-[#1976D2]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}>
              <i className={opt.icon} />{opt.label}
            </button>
          ))}
        </div>

        {dataLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : dataError ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-xl text-red-400" />
            </div>
            <p className="text-sm text-gray-500">{dataError}</p>
            <button onClick={loadPatients} className="mt-3 px-4 py-2 rounded-xl text-xs font-medium text-[#1976D2] bg-blue-50 cursor-pointer whitespace-nowrap">
              다시 시도
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">{filtered.length}명의 환자를 찾았어요</p>
            <div className="space-y-3">
              {filtered.map((p) => {
                const daysUntil = getDaysUntilStart(p.careStartDate);
                return (
                  <div key={p.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-[#E3F2FD] flex items-center justify-center shrink-0">
                        <i className="ri-user-3-line text-xl text-[#1976D2]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-sm font-bold text-gray-900">{p.name}</h3>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500">{p.gender} · {p.age}세</span>
                        </div>
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <i className={`${p.locationType === '집' ? 'ri-home-5-line' : 'ri-hospital-line'} text-gray-400`} />
                          <span className="truncate">{p.hospital}</span>
                          {p.locationType && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 shrink-0 whitespace-nowrap">
                              {p.locationType === '집' ? '자택' : '병원'}
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[#F57C00] font-medium">{p.disease}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-0.5 text-[10px] text-gray-600">
                          <i className="ri-map-pin-line text-[#1976D2] text-xs" />
                          <span className="font-bold">{formatDistance(p.distance)}</span>
                        </span>
                        <span className="flex items-center gap-1 text-[10px]">
                          <span className="text-gray-400">희망 시작:</span>
                          <span className={`font-bold ${daysUntil <= 3 && daysUntil >= 0 ? 'text-[#F57C00]' : 'text-gray-600'}`}>
                            {daysUntil === 0 ? '내일부터' : daysUntil < 999 ? `${daysUntil}일 후` : '협의'}
                          </span>
                        </span>
                        {p.careStartDate && (
                          <span className="text-[10px] text-gray-400">{p.careStartDate}</span>
                        )}
                      </div>
                      {MATCHING_ENABLED ? (
                        <button onClick={() => openProposal(p)}
                          className="px-4 py-2 rounded-lg text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                          style={{ backgroundColor: '#1976D2' }}>
                          매칭 제안하기
                        </button>
                      ) : (
                        <span className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-100 text-gray-400 whitespace-nowrap">
                          준비중
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {filtered.length === 0 && !dataLoading && (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-xl text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">조건에 맞는 환자가 없어요</p>
                <p className="text-xs text-gray-400 mt-1">검색어나 필터를 변경해보세요</p>
              </div>
            )}
          </>
        )}

        {/* 매칭 제안 팝업 */}
        {showProposal && selectedPatient && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setShowProposal(false)}>
            <div className="bg-white w-full max-w-md rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">매칭 제안하기</h2>
                <button onClick={() => setShowProposal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-2xl text-[#1976D2]" />
                  </div>
                  <p className="text-lg font-extrabold text-gray-900 mb-1">매칭 제안 완료!</p>
                  <p className="text-sm text-gray-500">환자가 수락하면 연락처가 공개됩니다.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                      <i className="ri-user-3-line text-[#1976D2]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{selectedPatient.name} 환자</p>
                      <p className="text-xs text-gray-500">{selectedPatient.hospital} · {selectedPatient.disease}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">시작일 <span className="text-red-400">*</span></label>
                      <input type="date" value={proposalForm.startDate} onChange={(e) => setProposalForm({ ...proposalForm, startDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">종료일 <span className="text-red-400">*</span></label>
                      <input type="date" value={proposalForm.endDate} onChange={(e) => setProposalForm({ ...proposalForm, endDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">희망 시급</label>
                      <select value={proposalForm.hourlyRate} onChange={(e) => setProposalForm({ ...proposalForm, hourlyRate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white">
                        {['13000','14000','15000','16000','18000','20000'].map((r) => (
                          <option key={r} value={r}>{parseInt(r).toLocaleString()}원</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">하루 간병 시간</label>
                      <select value={proposalForm.dailyHours} onChange={(e) => setProposalForm({ ...proposalForm, dailyHours: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white">
                        {['4','6','8','10','12','24'].map((h) => (<option key={h} value={h}>{h}시간</option>))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">제안 메시지</label>
                    <textarea value={proposalForm.notes} onChange={(e) => setProposalForm({ ...proposalForm, notes: e.target.value })}
                      placeholder="간병 경험, 가능한 사항 등을 적어주세요" rows={3} maxLength={500}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#1976D2] bg-white resize-none" />
                  </div>
                  {proposalForm.startDate && proposalForm.endDate && (
                    <div className="bg-[#E3F2FD] rounded-xl p-3">
                      <p className="text-xs text-gray-500">예상 간병비</p>
                      <p className="text-lg font-extrabold text-[#1976D2]">
                        {(parseInt(proposalForm.hourlyRate) * parseInt(proposalForm.dailyHours || '8') *
                          Math.max(1, Math.ceil((new Date(proposalForm.endDate).getTime() - new Date(proposalForm.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1)
                        ).toLocaleString()}원
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">간병비는 환자와 직접 정산합니다</p>
                    </div>
                  )}
                  {submitError && <p className="text-xs text-red-500 text-center">{submitError}</p>}
                  <button onClick={handleSubmitProposal} disabled={submitting}
                    className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
                    style={{ backgroundColor: '#1976D2' }}>
                    {submitting ? '제안 중...' : '매칭 제안하기'}
                  </button>
                  <p className="text-[10px] text-gray-400 text-center px-4">
                    환자가 수락하면 연락처가 공개되며, 간병비는 당사자 간 직접 정산합니다.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}